import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  EmailAuthProvider,
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  reauthenticateWithCredential,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as firebaseSignOut,
  updatePassword,
  updateProfile,
  type User,
} from "firebase/auth";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";

import { auth, db, isFirebaseConfigured, storage } from "../lib/firebase";
import { syncCommunityAuthorProfile } from "../services/communityService";

export const TCG_INTEREST_OPTIONS = [
  "MTG",
  "Pokémon",
  "One Piece",
  "DragonBall",
  "Gundam",
  "UA",
  "Yu-Gi-Oh!",
  "Digimon",
  "Lorcana",
] as const;

export const SPEND_OPTIONS = ["0-$1000", "$1000-$5000", "$10K above"] as const;

export const AVATAR_MAX_BYTES = 2 * 1024 * 1024;
export const AVATAR_ACCEPT = "image/jpeg,image/png,image/webp";

export type TcgInterest = (typeof TCG_INTEREST_OPTIONS)[number];
export type SpendRange = (typeof SPEND_OPTIONS)[number];

export type UserPreferences = {
  tcgInterest: TcgInterest;
  monthlySpend: SpendRange;
};

type AuthContextValue = {
  user: User | null;
  preferences: UserPreferences | null;
  loading: boolean;
  isConfigured: boolean;
  hasPasswordProvider: boolean;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signUpWithEmail: (
    email: string,
    password: string,
    preferences: UserPreferences,
  ) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
  savePreferences: (preferences: UserPreferences) => Promise<void>;
  updateDisplayName: (displayName: string) => Promise<void>;
  updateAvatar: (file: File) => Promise<void>;
  changePassword: (
    currentPassword: string,
    newPassword: string,
  ) => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

const PREFS_STORAGE_PREFIX = "ftcg_user_prefs:";

function prefsKey(uid: string) {
  return `${PREFS_STORAGE_PREFIX}${uid}`;
}

function readLocalPrefs(uid: string): UserPreferences | null {
  try {
    const raw = localStorage.getItem(prefsKey(uid));
    if (!raw) return null;
    return JSON.parse(raw) as UserPreferences;
  } catch {
    return null;
  }
}

function writeLocalPrefs(uid: string, preferences: UserPreferences) {
  localStorage.setItem(prefsKey(uid), JSON.stringify(preferences));
}

async function loadPreferences(uid: string): Promise<UserPreferences | null> {
  const local = readLocalPrefs(uid);
  if (!db) return local;

  try {
    const snap = await getDoc(doc(db, "users", uid));
    if (snap.exists()) {
      const data = snap.data() as Partial<UserPreferences>;
      if (data.tcgInterest && data.monthlySpend) {
        const prefs = {
          tcgInterest: data.tcgInterest,
          monthlySpend: data.monthlySpend,
        } as UserPreferences;
        writeLocalPrefs(uid, prefs);
        return prefs;
      }
    }
  } catch {
    // Firestore may not be enabled yet; fall back to local cache.
  }

  return local;
}

async function persistPreferences(uid: string, preferences: UserPreferences) {
  writeLocalPrefs(uid, preferences);
  if (!db) return;

  try {
    await setDoc(
      doc(db, "users", uid),
      {
        ...preferences,
        updatedAt: new Date().toISOString(),
      },
      { merge: true },
    );
  } catch {
    // Keep local prefs if Firestore write fails.
  }
}

function requireAuth() {
  if (!isFirebaseConfigured || !auth) {
    throw new Error(
      "Firebase is not configured. Add your Firebase keys to .env and restart the app.",
    );
  }
  return auth;
}

function userHasPasswordProvider(user: User | null) {
  return Boolean(user?.providerData.some((p) => p.providerId === "password"));
}

function assertAvatarFile(file: File) {
  const okType = ["image/jpeg", "image/png", "image/webp"].includes(file.type);
  if (!okType) {
    throw new Error("Avatar must be a JPG, JPEG, PNG, or WebP image.");
  }
  if (file.size > AVATAR_MAX_BYTES) {
    throw new Error("Avatar must be smaller than 2MB.");
  }
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [preferences, setPreferences] = useState<UserPreferences | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    const firebaseAuth = requireAuth();
    if (!firebaseAuth.currentUser) {
      setUser(null);
      return null;
    }
    await firebaseAuth.currentUser.reload();
    const next = firebaseAuth.currentUser;
    setUser(next);
    return next;
  }, []);

  useEffect(() => {
    if (!auth) {
      setLoading(false);
      return;
    }

    const unsub = onAuthStateChanged(auth, async (nextUser) => {
      setUser(nextUser);
      if (nextUser) {
        const prefs = await loadPreferences(nextUser.uid);
        setPreferences(prefs);
      } else {
        setPreferences(null);
      }
      setLoading(false);
    });

    return unsub;
  }, []);

  const signInWithEmail = useCallback(
    async (email: string, password: string) => {
      const firebaseAuth = requireAuth();
      await signInWithEmailAndPassword(firebaseAuth, email.trim(), password);
    },
    [],
  );

  const signUpWithEmail = useCallback(
    async (
      email: string,
      password: string,
      nextPreferences: UserPreferences,
    ) => {
      const firebaseAuth = requireAuth();
      const credential = await createUserWithEmailAndPassword(
        firebaseAuth,
        email.trim(),
        password,
      );
      const displayName = email.trim().split("@")[0] || "Collector";
      await updateProfile(credential.user, { displayName });
      await persistPreferences(credential.user.uid, nextPreferences);
      setPreferences(nextPreferences);
    },
    [],
  );

  const signInWithGoogle = useCallback(async () => {
    const firebaseAuth = requireAuth();
    const provider = new GoogleAuthProvider();
    await signInWithPopup(firebaseAuth, provider);
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    const firebaseAuth = requireAuth();
    await sendPasswordResetEmail(firebaseAuth, email.trim());
  }, []);

  const signOut = useCallback(async () => {
    const firebaseAuth = requireAuth();
    await firebaseSignOut(firebaseAuth);
  }, []);

  const savePreferences = useCallback(
    async (nextPreferences: UserPreferences) => {
      if (!user) {
        throw new Error("You must be signed in to save preferences.");
      }
      await persistPreferences(user.uid, nextPreferences);
      setPreferences(nextPreferences);
    },
    [user],
  );

  const updateDisplayName = useCallback(
    async (displayName: string) => {
      if (!user) throw new Error("You must be signed in.");
      const trimmed = displayName.trim();
      if (!trimmed) throw new Error("Nickname cannot be empty.");
      if (trimmed.length > 40) {
        throw new Error("Nickname must be 40 characters or fewer.");
      }

      await updateProfile(user, { displayName: trimmed });
      const next = await refreshUser();
      if (next) {
        await syncCommunityAuthorProfile({
          userId: next.uid,
          authorName: trimmed,
          authorPhotoURL: next.photoURL ?? null,
        });
      }
    },
    [user, refreshUser],
  );

  const updateAvatar = useCallback(
    async (file: File) => {
      if (!user) throw new Error("You must be signed in.");
      if (!storage) {
        throw new Error(
          "Firebase Storage is not configured. Enable Storage in Firebase Console.",
        );
      }
      assertAvatarFile(file);

      const ext =
        file.type === "image/png"
          ? "png"
          : file.type === "image/webp"
            ? "webp"
            : "jpg";
      const storageRef = ref(storage, `avatars/${user.uid}/avatar.${ext}`);
      await uploadBytes(storageRef, file, { contentType: file.type });
      const photoURL = await getDownloadURL(storageRef);

      await updateProfile(user, { photoURL });
      const next = await refreshUser();
      if (next) {
        await syncCommunityAuthorProfile({
          userId: next.uid,
          authorName:
            next.displayName?.trim() ||
            next.email?.split("@")[0] ||
            "Collector",
          authorPhotoURL: photoURL,
        });
      }
    },
    [user, refreshUser],
  );

  const changePassword = useCallback(
    async (currentPassword: string, newPassword: string) => {
      if (!user?.email) throw new Error("You must be signed in.");
      if (!userHasPasswordProvider(user)) {
        throw new Error(
          "Password changes are only available for email/password accounts.",
        );
      }
      if (newPassword.length < 6) {
        throw new Error("New password should be at least 6 characters.");
      }

      const credential = EmailAuthProvider.credential(
        user.email,
        currentPassword,
      );
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);
    },
    [user],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      preferences,
      loading,
      isConfigured: isFirebaseConfigured,
      hasPasswordProvider: userHasPasswordProvider(user),
      signInWithEmail,
      signUpWithEmail,
      signInWithGoogle,
      resetPassword,
      signOut,
      savePreferences,
      updateDisplayName,
      updateAvatar,
      changePassword,
    }),
    [
      user,
      preferences,
      loading,
      signInWithEmail,
      signUpWithEmail,
      signInWithGoogle,
      resetPassword,
      signOut,
      savePreferences,
      updateDisplayName,
      updateAvatar,
      changePassword,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
}
